import React, { useEffect, useRef } from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import Skeleton from '@/components/@IntranetLibrary/Skeleton';
import { useFetchDeployUrl } from '@/hooks/board/useFetchDeployUrl';
import { useFullScreen } from '@/hooks/useFullScreen';
import FullScreenButton from '@/components/FullScreen/FullScreenButton';
import RefreshIcon from '@/public/icons/refresh.svg';

interface DeployedCodeViewerProps {
  boardId: string;
  previewUrl?: string;
  updatedAt?: string;
  enableFullScreen: boolean;
  /** When true, container uses flex:1 and fills parent. Default: false (existing behavior) */
  fillContainer?: boolean;
}

const DeployedCodeViewer: React.FC<DeployedCodeViewerProps> = ({
  boardId,
  previewUrl,
  updatedAt,
  enableFullScreen,
  fillContainer = false,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const { isFullScreen, toggleFullScreen } = useFullScreen(containerRef);

  const {
    isLoading,
    url: deployUrl,
    error,
    errorDetail,
    fetchDeployUrl,
  } = useFetchDeployUrl();

  useEffect(() => {
    if (!boardId) return;

    if (previewUrl) {
      // previewUrl이 있으면 직접 사용 (CreateBoardModal 미리보기에서)
      return;
    }

    // BoardDetail에서 사용 시 배포 URL 가져오기
    fetchDeployUrl(boardId);
  }, [boardId, updatedAt, previewUrl, fetchDeployUrl]);

  const displayUrl = previewUrl || deployUrl;

  if (isLoading) {
    return (
      <Box
        sx={
          fillContainer
            ? {
                flex: '1 1 0',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 0,
              }
            : { padding: '24px 0' }
        }
      >
        <Skeleton
          variant="rect"
          width="100%"
          height={fillContainer ? '100%' : 700}
          sx={{
            flex: fillContainer ? '1 1 0' : undefined,
            minHeight: fillContainer ? 0 : undefined,
          }}
        />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          ...(fillContainer
            ? {
                flex: '1 1 0',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                minHeight: 0,
              }
            : { marginTop: '24px' }),
          padding: '60px 20px',
          textAlign: 'center',
          backgroundColor: 'var(--dk-bg-subtle, #F9FAFB)',
          borderRadius: '8px',
          border: '1px solid var(--dk-line, #E7E9EC)',
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: 'var(--dk-text-secondary, #888D96)',
            fontSize: '16px',
            fontWeight: 500,
          }}
        >
          {error}
        </Typography>
        {errorDetail && (
          <Typography
            sx={{
              color: 'var(--dk-text-secondary, #888D96)',
              fontSize: '14px',
              fontWeight: 400,
              marginTop: '8px',
              whiteSpace: 'pre-wrap',
            }}
          >
            {errorDetail}
          </Typography>
        )}

        <Button
          variant={'outlined'}
          size={'large'}
          onClick={() => fetchDeployUrl(boardId)}
          disabled={isLoading}
          startIcon={<RefreshIcon width={20} height={20} />}
          sx={{ marginTop: '12px' }}
        >
          새로고침
        </Button>
      </Box>
    );
  }

  if (!displayUrl) {
    return null;
  }

  return (
    <Box
      ref={containerRef}
      sx={
        fillContainer
          ? {
              position: 'relative',
              flex: '1 1 0',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              overflow: 'hidden',
              width: '100%',
              minWidth: 0,
              alignSelf: 'stretch',
            }
          : {
              position: 'relative',
              marginTop: '24px',
            }
      }
    >
      {enableFullScreen && (
        <Stack
          style={{
            position: 'sticky',
            right: `calc(var(--board-detail-padding-X, 32px) + 8px)`,
            top: `calc(60px + 8px + 24px)`,
            float: 'right',
            marginBottom: 'calc(-44px - 8px)',
          }}
          direction="column"
          justifyContent="center"
          alignItems="center"
        >
          <FullScreenButton
            isFullScreen={isFullScreen}
            onClick={toggleFullScreen}
          />
        </Stack>
      )}

      <iframe
        src={displayUrl}
        style={
          fillContainer
            ? {
                width: '100%',
                flex: '1 1 0',
                minHeight: 0,
                height: '100%',
                border: 'none',
                display: 'block',
              }
            : {
                width: '100%',
                minHeight: '700px',
                height: '100%',
                border: 'none',
              }
        }
        title="Code Preview"
      />
    </Box>
  );
};

export default DeployedCodeViewer;
